/**
 * PhytochemistryMap
 *
 * Visual SVG graph: plant → bioactive compounds → human protein targets.
 * Inspired by the "digital twin" paradigm (Mantis Biotech) but applied to
 * herbolaria mexicana: shows the mechanism of action of a medicinal plant
 * as an animated three-layer constellation.
 *
 * Layers (left to right):
 *   1. Plant node (center-left)
 *   2. Phytochemical compounds (middle column)
 *   3. Human protein targets with UniProt IDs (right column)
 *
 * Edges are drawn as smooth bezier curves with a subtle pulse animation
 * to convey biological "flow". Click a node to see its details.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import molecularService from '../../services/molecularService';

const EDGE_COLORS = {
  contains: '#10b981',     // emerald — plant → compound
  inhibits: '#B8531F',     // copal — compound inhibits protein (red reserved for destructive)
  activates: '#3b82f6',    // blue — compound activates protein
  modulates: '#2C7D8C',    // turquesa — modulator (science/data)
  binds: '#f59e0b',        // amber — binder
  category_target: '#6b7280', // gray fallback
};

const CONFIDENCE_OPACITY = {
  high: 1.0,
  medium: 0.65,
  exploratory: 0.4,
};

const PhytochemistryMap = ({ plantId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);

  useEffect(() => {
    if (!plantId) return;
    let cancelled = false;

    setLoading(true);
    setError(null);
    molecularService
      .getPhytochemistryMap(plantId)
      .then((payload) => {
        if (cancelled) return;
        setData(payload);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response?.data?.detail || 'No se pudo cargar el mapa fitoquímico');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [plantId]);

  // Compute node positions deterministically based on type/index.
  const layout = useMemo(() => {
    if (!data) return null;
    const plants = data.nodes.filter((n) => n.type === 'plant');
    const compounds = data.nodes.filter((n) => n.type === 'compound');
    const proteins = data.nodes.filter((n) => n.type === 'protein');

    const width = 720;
    const height = Math.max(360, Math.max(compounds.length, proteins.length) * 80 + 80);

    const positions = {};
    plants.forEach((node, i) => {
      positions[node.id] = { x: 80, y: height / 2, layer: 0 };
    });
    compounds.forEach((node, i) => {
      const n = compounds.length;
      const y = n === 1 ? height / 2 : 60 + (i * (height - 120)) / (n - 1);
      positions[node.id] = { x: width / 2, y, layer: 1 };
    });
    proteins.forEach((node, i) => {
      const n = proteins.length;
      const y = n === 1 ? height / 2 : 60 + (i * (height - 120)) / (n - 1);
      positions[node.id] = { x: width - 80, y, layer: 2 };
    });

    return { width, height, positions };
  }, [data]);

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.spinner} />
        <p>Construyendo el mapa fitoquímico…</p>
      </div>
    );
  }

  if (error) {
    return <p style={styles.error}>{error}</p>;
  }

  if (!data || !layout) return null;

  const handleNodeClick = (node) => {
    setSelectedNode(selectedNode?.id === node.id ? null : node);
    setSelectedEdge(null);
  };

  const handleEdgeClick = (edge) => {
    setSelectedEdge(selectedEdge === edge ? null : edge);
    setSelectedNode(null);
  };

  const { width, height, positions } = layout;
  const isFallback = data.source === 'category_fallback';

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h3 style={styles.title}>
          🧬 Mapa fitoquímico de {data.common_name}
        </h3>
        <p style={styles.subtitle}>
          De la planta a las moléculas a las proteínas humanas que modulan.
          {isFallback && (
            <span style={styles.fallbackNote}>
              {' '}Curación específica próximamente — mostramos dianas por categoría.
            </span>
          )}
        </p>
      </div>

      <div style={styles.legend}>
        <LegendItem color={EDGE_COLORS.contains} label="contiene" />
        <LegendItem color={EDGE_COLORS.inhibits} label="inhibe" />
        <LegendItem color={EDGE_COLORS.activates} label="activa" />
        <LegendItem color={EDGE_COLORS.modulates} label="modula" />
        <LegendItem color={EDGE_COLORS.binds} label="se une" />
      </div>

      <div style={styles.svgWrapper}>
        <svg
          viewBox={`0 0 ${width} ${height}`}
          style={styles.svg}
          preserveAspectRatio="xMidYMid meet"
        >
          {/* Subtle background grid */}
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#f3f4f6" strokeWidth="1" />
            </pattern>
            <filter id="glow">
              <feGaussianBlur stdDeviation="3" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          <rect width={width} height={height} fill="url(#grid)" />

          {/* Layer labels */}
          <text x={80} y={28} textAnchor="middle" style={styles.layerLabel}>
            Planta
          </text>
          <text x={width / 2} y={28} textAnchor="middle" style={styles.layerLabel}>
            Compuestos
          </text>
          <text x={width - 80} y={28} textAnchor="middle" style={styles.layerLabel}>
            Proteínas humanas
          </text>

          {/* Edges */}
          {data.edges.map((edge, idx) => {
            const s = positions[edge.source];
            const t = positions[edge.target];
            if (!s || !t) return null;
            const color = EDGE_COLORS[edge.kind] || EDGE_COLORS.category_target;
            const opacity = CONFIDENCE_OPACITY[edge.confidence] || 0.75;
            const midX = (s.x + t.x) / 2;
            const path = `M ${s.x} ${s.y} C ${midX} ${s.y}, ${midX} ${t.y}, ${t.x} ${t.y}`;
            const isSelected = selectedEdge === edge;

            return (
              <g key={`edge-${idx}`}>
                <motion.path
                  d={path}
                  fill="none"
                  stroke={color}
                  strokeWidth={isSelected ? 3 : 1.8}
                  strokeOpacity={opacity}
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 0.9, delay: idx * 0.04, ease: 'easeOut' }}
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleEdgeClick(edge)}
                />
                {/* Animated pulse dot along edge */}
                <motion.circle
                  r="2.5"
                  fill={color}
                  opacity={0.8}
                  initial={{ offsetDistance: '0%' }}
                  animate={{ offsetDistance: '100%' }}
                  transition={{
                    duration: 3.5,
                    repeat: Infinity,
                    delay: idx * 0.3,
                    ease: 'linear',
                  }}
                  style={{ offsetPath: `path("${path}")` }}
                />
              </g>
            );
          })}

          {/* Nodes */}
          {data.nodes.map((node) => {
            const pos = positions[node.id];
            if (!pos) return null;
            const isSelected = selectedNode?.id === node.id;
            return (
              <NodeMarker
                key={node.id}
                node={node}
                position={pos}
                selected={isSelected}
                onClick={() => handleNodeClick(node)}
              />
            );
          })}
        </svg>
      </div>

      {/* Detail panel */}
      <AnimatePresence>
        {(selectedNode || selectedEdge) && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12 }}
            style={styles.detailPanel}
          >
            {selectedNode && <NodeDetail node={selectedNode} />}
            {selectedEdge && <EdgeDetail edge={selectedEdge} />}
          </motion.div>
        )}
      </AnimatePresence>

      <p style={styles.footer}>
        Datos fitoquímicos curados manualmente sobre PubChem · FooDB · UniProt.
        Estructuras 3D vía AlphaFold (DeepMind · EBI).
      </p>
    </div>
  );
};

const NodeMarker = ({ node, position, selected, onClick }) => {
  const config = {
    plant: { color: '#10b981', secondary: '#d1fae5', emoji: '🌿', radius: 30 },
    compound: { color: '#f59e0b', secondary: '#fef3c7', emoji: '⚗️', radius: 24 },
    protein: { color: '#2C7D8C', secondary: '#D7E9EC', emoji: '🧬', radius: 26 },
  }[node.type] || { color: '#6b7280', secondary: '#f3f4f6', emoji: '·', radius: 20 };

  const r = selected ? config.radius + 4 : config.radius;

  // Label trimming
  const label = node.label?.length > 18 ? `${node.label.slice(0, 16)}…` : node.label;

  return (
    <motion.g
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.4, delay: 0.1, type: 'spring', stiffness: 200 }}
      style={{ cursor: 'pointer' }}
      onClick={onClick}
    >
      <circle
        cx={position.x}
        cy={position.y}
        r={r + 6}
        fill={config.color}
        opacity={selected ? 0.25 : 0.12}
      />
      <circle
        cx={position.x}
        cy={position.y}
        r={r}
        fill={config.secondary}
        stroke={config.color}
        strokeWidth={selected ? 3 : 2}
        filter={selected ? 'url(#glow)' : undefined}
      />
      <text
        x={position.x}
        y={position.y + 6}
        textAnchor="middle"
        fontSize="20"
        style={{ userSelect: 'none' }}
      >
        {config.emoji}
      </text>
      <text
        x={position.x}
        y={position.y + r + 18}
        textAnchor="middle"
        fontSize="11"
        fontWeight={600}
        fill="#111827"
        style={{ userSelect: 'none' }}
      >
        {label}
      </text>
      {node.uniprot_id && (
        <text
          x={position.x}
          y={position.y + r + 32}
          textAnchor="middle"
          fontSize="9"
          fill="#6b7280"
          style={{ userSelect: 'none' }}
        >
          {node.uniprot_id}
        </text>
      )}
    </motion.g>
  );
};

const NodeDetail = ({ node }) => (
  <div>
    <div style={styles.detailHeader}>
      <span style={styles.detailType}>{nodeTypeLabel(node.type)}</span>
      <h4 style={styles.detailTitle}>{node.label}</h4>
      {node.subtitle && <p style={styles.detailSubtitle}>{node.subtitle}</p>}
    </div>
    {node.effect && <p style={styles.detailBody}>{node.effect}</p>}
    {node.uniprot_id && (
      <a
        href={`https://www.uniprot.org/uniprot/${node.uniprot_id}`}
        target="_blank"
        rel="noopener noreferrer"
        style={styles.externalLink}
      >
        Ver en UniProt →
      </a>
    )}
  </div>
);

const EdgeDetail = ({ edge }) => (
  <div>
    <div style={styles.detailHeader}>
      <span style={{ ...styles.detailType, background: EDGE_COLORS[edge.kind] }}>
        {edgeLabel(edge.kind)}
      </span>
      {edge.confidence && (
        <span style={styles.confidenceBadge}>Confianza: {confidenceLabel(edge.confidence)}</span>
      )}
    </div>
    {edge.evidence && <p style={styles.detailBody}>{edge.evidence}</p>}
  </div>
);

const LegendItem = ({ color, label }) => (
  <div style={styles.legendItem}>
    <span style={{ ...styles.legendSwatch, background: color }} />
    <span style={styles.legendLabel}>{label}</span>
  </div>
);

const nodeTypeLabel = (type) => ({
  plant: 'Planta medicinal',
  compound: 'Fitoquímico',
  protein: 'Proteína diana',
}[type] || type);

const edgeLabel = (kind) => ({
  contains: 'contiene',
  inhibits: 'inhibe',
  activates: 'activa',
  modulates: 'modula',
  binds: 'se une',
  category_target: 'diana categórica',
}[kind] || kind);

const confidenceLabel = (c) => ({
  high: 'alta',
  medium: 'media',
  exploratory: 'exploratoria',
}[c] || c);

const styles = {
  container: { display: 'flex', flexDirection: 'column', gap: 16 },
  header: {},
  title: { margin: 0, fontSize: 18, fontWeight: 700, color: '#111827' },
  subtitle: { marginTop: 4, fontSize: 13, color: '#4b5563', lineHeight: 1.5 },
  fallbackNote: { color: '#92400e', fontStyle: 'italic' },
  legend: { display: 'flex', flexWrap: 'wrap', gap: 12 },
  legendItem: { display: 'flex', alignItems: 'center', gap: 6 },
  legendSwatch: { width: 16, height: 3, borderRadius: 2, display: 'inline-block' },
  legendLabel: { fontSize: 11, color: '#374151' },
  svgWrapper: {
    width: '100%',
    background: '#ECDDC4',
    borderRadius: 12,
    border: '1px solid #e5e7eb',
    padding: 8,
  },
  svg: { width: '100%', height: 'auto', display: 'block' },
  layerLabel: {
    fontSize: 11,
    fill: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: 1,
    fontWeight: 600,
  },
  detailPanel: {
    background: '#ECDDC4',
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    padding: 16,
  },
  detailHeader: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  detailType: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: '#fff',
    background: '#2C7D8C',
    padding: '3px 8px',
    borderRadius: 4,
  },
  detailTitle: { margin: '6px 0 0 0', fontSize: 16, fontWeight: 700, color: '#111827' },
  detailSubtitle: { margin: '4px 0 0 0', fontSize: 12, color: '#6b7280', fontStyle: 'italic' },
  detailBody: { fontSize: 13, color: '#374151', lineHeight: 1.55, margin: '10px 0' },
  confidenceBadge: {
    fontSize: 11,
    color: '#374151',
    background: '#F4EBD9',
    border: '1px solid #d1d5db',
    padding: '2px 8px',
    borderRadius: 10,
  },
  externalLink: {
    display: 'inline-block',
    marginTop: 6,
    fontSize: 12,
    color: '#059669',
    textDecoration: 'none',
    fontWeight: 600,
  },
  footer: { fontSize: 11, color: '#9ca3af', fontStyle: 'italic', margin: 0 },
  loading: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: 40, color: '#6b7280' },
  spinner: {
    width: 32,
    height: 32,
    border: '3px solid #e5e7eb',
    borderTopColor: '#10b981',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  error: { color: '#b91c1c', padding: 12 },
};

export default PhytochemistryMap;
